import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Dialog,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  DialogActions,
  DialogContent,
  Button as MuiButton,
  Tooltip
} from '@material-ui/core';
import { t } from 'ttag';
import clsx from 'clsx';
import useStyles from './styles';
import Close from '@icons/Close';
import {
  Button,
  useConfirmation,
  IconButton,
  Select,
} from '@coolremote/shared';
import FlowButton from '@components/FlowButton';
import Checkbox from '@cool_widgets/CoolCheckbox';
import { useStoreState, useStoreActions } from '@models/RootStore';
import useBacNet from '@hooks/useBacNet';
import useTypes from '@hooks/useTypes';
import { smartConvertToStringFormats } from '@utils/StringUtils';
import DynamicCircleIcon from '@components/DynamicCircleIcon';
import ActivePrioritiesDialog from './ActivePrioritiesDialog';
import UniversalMappingSettings from './UniversalMappingSettings';
import { Refresh } from '@icons/index';
import SaveIcon from '@material-ui/icons/Save';
import SaveAltIcon from '@material-ui/icons/SaveAlt';
import LightTooltip from '@components/Tooltip/LightTooltip';

const TEMPERATURE_UNITS = {
  62: 'degrees-celsius',
  64: 'degrees-fahrenheit',
};

const OBJECT_TYPES = {
  binary: 1,
  'reverse_Binary': 2,
  enumeration: 4,
  value: 5
};

const defaultValueTypeEnums = {
  min: 0,
  max: 0,
  step: 0,
};

/**A function that gets a binary value and return 'Yes' or 'No'. */
const getBinaryParamDisplayValue = (value) => value ? t`Yes` : t`No`;

/**
 * Determines the state of a COV field based on whether COV is supported.
 * - If `isCovSupported` is `false`, the field is disabled and set to a default value of `'2'`(No).
 * - For `true` or `undefined` values, the field is enabled with an empty string as its value.
 *
 * @param {boolean|undefined} isCovSupported - Flag indicating COV support status.
 * @returns {{ disabled: boolean, value: string }} - Object with `disabled` status and `value` for the COV field.
 */
const getCovFieldState = (isCovSupported) => {

  if (isCovSupported === false) {
    return {
      disabled: true,
      value: 2,
    };
  }

  return {
    disabled: false,
    value: '',
  };


};

/**
 * Returns a visibility map for an enumeration.
 *
 * If a non-empty visibility map is provided, it is returned unchanged.
 * Otherwise, if a valid enumeration object is provided, a new map is created
 * where each key from the enumeration is set to true. If the enumeration is
 * absent or not an object, an empty map is returned.
 *
 * This function does not mutate its inputs.
 *
 * @param {Record<string, boolean> | null | undefined} enumerationVisibility - Optional precomputed visibility map; returned as-is if non-empty.
 * @param {Record<string, any> | null | undefined} enumeration - Enumeration-like object whose keys will be marked visible.
 * @returns {Record<string, boolean>} A visibility map keyed by enumeration keys with boolean values.
 */
const getEnumerationVisibility = (enumerationVisibility, enumeration) => {

  if (enumerationVisibility && Object.keys(enumerationVisibility).length) {
    return enumerationVisibility;
  }

  if (!enumeration || typeof enumeration !== 'object') {
    return {};
  }

  return Object.keys(enumeration).reduce((map, key) => {
    map[key] = true;
    return map;
  }, {});
};

/**
 * ParamMappingDialog component for configuring and mapping BACnet object parameters to a template.
 *
 * @param {Object} props
 * @param {Object} props.bacNetObject - The BACnet object containing parameter data to be mapped.
 * @param {Object} props.template - The template containing existing mappings and data.
 * @param {function} props.handleSave - Callback to handle saving the changes made in the dialog.
 * @param {function} props.handleClose - Callback to handle closing the dialog.
 * @param {string} props.bacnetUnitId - The ID of the BACnet unit.
 */

const ParamMappingDialog = ({
  bacNetObject,
  template,
  handleSave,
  handleClose,
  bacnetUnitId,
  currentValue,
  isRefreshingValue,
  onRefreshValue,
  isRawSupported,
}) => {
  const classes = useStyles();
  const confirm = useConfirmation();
  const { getBacnetIconInformation, writeObjectValues } = useBacNet();

  const {
    name,
    objectId,
    enumeration,
    objectType,
    isCovSupported,
    bacnetProps,
    bacnetType,
    propertyTags,
    isCommandable,
  } = bacNetObject;

  const {
    color,
    shortName,
    name: fullName,
  } = getBacnetIconInformation(bacnetType);

  const { UNITS } = bacnetProps;

  const {
    bacnetObjectTypes,
    bacnetMeasurementUnits,
    bacnetUnitTypes,
    bacnetPriorities,
    defaultBinaryEnums,
  } = useTypes();

  const { defaultReverseBinaryEnums, bacnetCovOptionsTypes } = useStoreState(
    (state) => state.types
  );

  const { addMessage } = useStoreActions((action) => action.errorMessage);

  const roTypes = {
    0: 'no',
    1: 'yes',
  };

  //This will be restored in the future. do not delete.
  /*  const bacnetUnitOptions = useMemo(
    () => Object.keys(bacnetUnitTypes),
    [bacnetUnitTypes]
  ); */

  const typeNameByTypeId = useMemo(() => {
    if (!bacnetUnitTypes) {
      return {};
    }

    return Object.values(bacnetUnitTypes).reduce((map, { value, label }) => {
      map[value] = label;
      return map;
    }, {});
  }, [bacnetUnitTypes]);

  const reverseBinaryOptions = useMemo(
    () => Object.values(defaultReverseBinaryEnums),
    [defaultReverseBinaryEnums]
  );

  const bacnetEnumerationsTemplate = useMemo(() => {
    if (!enumeration) {
      return [];
    }
    return Object.entries(enumeration).map(([key, value]) => ({ value, key }));
  }, [enumeration]);

  const binaryOptions = useMemo(
    () => Object.values(defaultBinaryEnums),
    [defaultBinaryEnums]
  );

  const unitsFieldOptions = useMemo(() => {
    return Object.entries(bacnetMeasurementUnits).map(([key, value]) => ({
      value: key,
      label: smartConvertToStringFormats(value).capitalized,
    }));
  }, [bacnetMeasurementUnits]);

  const tempFieldOptions = useMemo(() => {
    return Object.entries(bacnetMeasurementUnits)
      .filter(([key]) => key in TEMPERATURE_UNITS)
      .map(([key, value]) => ({
        value: key,
        label: smartConvertToStringFormats(value).capitalized,
      }));
  }, [bacnetMeasurementUnits]);

  const bacnetCovOptions = useMemo(
    () => Object.keys(bacnetCovOptionsTypes),
    [bacnetCovOptionsTypes]
  );

  const priorityFieldsOptions = useMemo(() => {
    return Object.entries(bacnetPriorities).map(([value, label]) => ({
      value,
      label,
    }));
  }, [bacnetPriorities]);

  const defaultValueTypeOption = useMemo(
    () => Object.keys(defaultValueTypeEnums),
    [defaultValueTypeEnums]
  );

  const covFieldsProps = getCovFieldState(isCovSupported);

  const [mappedObject, setMappedObject] = useState({
    name: name,
    cov: covFieldsProps.value,
    priority: '16',
    type: objectType,
    customEnum: {},
    customEnumVisibility: {},
    readOnly: false,
    errorPointEnabled: false,
    errorPointValue: 0,
    isCommandable: false,
    valueTag: 0,
  });

  const [dirty, setDirty] = useState(false);
  const [showActivePrioritiesDialog, setShowActivePrioritiesDialog] =
    useState(false);

  const normalizeValue = useCallback(
    (val) => {
      if (val === undefined || val === null || typeof val !== 'number') {
        return val;
      }

      const step = parseFloat(mappedObject?.step);
      if (!isNaN(step) && step > 0) {
        // Round to the nearest multiple of step, and fix floating point precision
        const result = Math.round(val / step) * step;
        return parseFloat(result.toFixed(10));
      }

      if (
        objectType === OBJECT_TYPES.binary ||
        objectType === OBJECT_TYPES.reverse_Binary ||
        objectType === OBJECT_TYPES.enumeration
      ) {
        return Math.round(val);
      }
      return Math.round(val * 1000) / 1000;
    },
    [objectType, mappedObject?.step]
  );

  const [savedValue, setSavedValue] = useState(
    currentValue !== undefined && currentValue !== null
      ? String(normalizeValue(currentValue))
      : ''
  );

  const [writeValue, setWriteValue] = useState(savedValue);

  useEffect(() => {
    if (currentValue !== undefined && currentValue !== null) {
      const normalized = String(normalizeValue(currentValue));
      setSavedValue(normalized);
      setWriteValue((prev) => (prev === '' ? normalized : prev));
    }
  }, [currentValue]);

  const displayedCurrentValue =
    savedValue !== ''
      ? isNaN(parseFloat(savedValue))
        ? savedValue
        : parseFloat(savedValue)
      : currentValue;

  const isWriteDisabled = mappedObject.readOnly || !isRawSupported;

  const isSameValue = writeValue === savedValue;

  const isSaveButtonDisabled =
    isWriteDisabled || isSameValue || writeValue === '' || isRefreshingValue;

  const formatValue = (val) => {
    if (val === null || val === undefined) {
      return '-';
    }
    return typeof val === 'number'
      ? Number.isInteger(val)
        ? val
        : val.toFixed(1)
      : val;
  };

  const getDisplayValue = (value) => {
    const normalizedValue = normalizeValue(value);
    if (
      mappedObject?.customEnum &&
      normalizedValue !== undefined &&
      normalizedValue !== null
    ) {
      const enumValue = mappedObject.customEnum[String(normalizedValue)];
      if (enumValue !== undefined) {
        return enumValue;
      }
    }

    // Fallback to built-in BACnet enumeration if available on the object
    if (
      bacNetObject.enumeration &&
      normalizedValue !== undefined &&
      normalizedValue !== null
    ) {
      return bacNetObject.enumeration[normalizedValue];
    }

    return formatValue(normalizedValue);
  };

  const handleSetValue = async () => {
    if (writeValue === '') {
      return;
    }
    const numericValue = parseFloat(writeValue);
    const valueToSend = isNaN(numericValue) ? writeValue : numericValue;
    try {
      const result = await writeObjectValues(bacNetObject.bacnetDevice, {
        [objectId]: {
          tag: mappedObject.valueTag || 0,
          value: valueToSend,
          priority: parseInt(mappedObject.priority, 10) || 16,
        },
      });
      const objectData = result?.objects?.[objectId];
      setSavedValue(writeValue);
      onRefreshValue({ stopPropagation: () => {} });
    } catch (e) {
      addMessage({ message: e.message || t`Failed to set object value` });
    }
  };

  const renderWriteValueControl = () => {
    if (
      objectType === OBJECT_TYPES.binary ||
      objectType === OBJECT_TYPES['reverse_Binary']
    ) {
      const entries = Object.entries(mappedObject.customEnum || {});
      return (
        <Select
          menuPortal
          value={writeValue}
          onChange={setWriteValue}
          disabled={isWriteDisabled}
          className={classes.writeValueSelect}
          classes={{ trigger: classes.writeValueSelectRoot }}
          options={entries.map(([key, label]) => ({ value: key, label }))}
        />
      );
    }

    if (objectType === OBJECT_TYPES.enumeration) {
      const entries = Object.entries(enumeration || {});
      return (
        <Select
          menuPortal
          value={writeValue}
          onChange={setWriteValue}
          disabled={isWriteDisabled}
          className={classes.writeValueSelect}
          classes={{ trigger: classes.writeValueSelectRoot }}
          options={entries.map(([key, label]) => ({ value: key, label }))}
        />
      );
    }

    return (
      <TextField
        variant="outlined"
        value={writeValue}
        onChange={(e) => setWriteValue(e.target.value)}
        disabled={isWriteDisabled}
        className={classes.writeValueInput}
        InputProps={{ classes: { root: classes.writeValueInputRoot } }}
      />
    );
  };

  const initFromBacnetObject = () => {
    const derivedMappedObject = {
      name: name,
      cov: covFieldsProps.value,
      priority: '16',
      type: objectType,
      customEnum: {},
      readOnly: bacNetObject?.isReadOnly,
    };

    if (objectType === OBJECT_TYPES.value) {
      const { minimumValue, maximumValue, valueStep } = bacNetObject;
      derivedMappedObject.units = UNITS;

      derivedMappedObject.customEnum = {
        min: minimumValue ?? 0,
        max: maximumValue ?? 0,
        step: valueStep ?? 1,
      };
    }

    if (
      objectType === OBJECT_TYPES.binary ||
      objectType === OBJECT_TYPES.reverse_Binary
    ) {
      const keyForOff = objectType === OBJECT_TYPES.binary ? 'off' : 'on';
      const keyForOn = objectType === OBJECT_TYPES.binary ? 'on' : 'off';

      derivedMappedObject.customEnum = {
        0: enumeration?.[0] ?? defaultBinaryEnums[keyForOff].label,
        1: enumeration?.[1] ?? defaultBinaryEnums[keyForOn].label,
      };
    }

    if (objectType === OBJECT_TYPES.enumeration) {
      derivedMappedObject.customEnum = enumeration;
      derivedMappedObject.customEnumVisibility = getEnumerationVisibility(
        mappedObject?.customEnumVisibility,
        enumeration
      );
    }

    if ('PRIORITY_ARRAY' in bacnetProps) {
      derivedMappedObject.isCommandable = true;
    }

    if (propertyTags?.[85]) {
      derivedMappedObject.valueTag = propertyTags[85];
    }

    setMappedObject(derivedMappedObject);
    setDirty(true);
  };

  useEffect(() => {
    if (objectId in template.data) {
      const newMappedObject = { ...mappedObject, ...template.data[objectId] };
      setMappedObject(newMappedObject);
    } else {
      initFromBacnetObject();
    }
  }, [template, bacNetObject]);

  /**
   * Handles changes to a field in the mappedObject.
   *
   * @param {string} field - The name of the field being changed.
   * @returns {function} - A function that takes an event and updates the corresponding field in mappedObject.
   */
  const handleChange = useCallback(
    (field) => (eventOrValue) => {
      setDirty(true);

      let value;
      let checked;
      let type;

      if (
        eventOrValue &&
        typeof eventOrValue === 'object' &&
        'target' in eventOrValue
      ) {
        type = eventOrValue.target.type;
        checked = eventOrValue.target.checked;
        value = eventOrValue.target.value;
      } else {
        value = eventOrValue;
      }

      setMappedObject((prevParam) => ({
        ...prevParam,
        [field]: type === 'checkbox' ? checked : value,
        customEnum: field === 'type' ? {} : prevParam.customEnum,
      }));
    },
    []
  );

  /**
   * Handles changes to enum fields in the mappedObject.
   *
   * @param {string} field - The name of the enum field being changed.
   * @returns {function} - A function that takes an event and updates the enum field in mappedObject.
   */
  const handleEnumChange = useCallback(
    (field) => (event) => {
      setDirty(true);
      const { value } = event.target;
      setMappedObject((prevParam) => ({
        ...prevParam,
        customEnum: {
          ...prevParam.customEnum,
          [field]: value,
        },
      }));
    },
    []
  );

  /**
   * Handles saving the changes made to the mappedObject.
   */
  const isValid = useMemo(() => {
    if (bacNetObject.isReadOnly) {
      return true;
    }
    const { step } = mappedObject.customEnum || {};
    return step === undefined || parseFloat(step) > 0;
  }, [mappedObject.customEnum, bacNetObject.isReadOnly]);

  /**
   * Handles saving the changes made to the mappedObject.
   */
  const handleSaveChanges = async () => {
    if (!isValid) {
      return;
    }

    const newData = {
      ...template.data,
      [objectId]: mappedObject,
    };

    handleSave(template.id, newData);
  };

  /**
   * Renders input fields for enum values.
   *
   * @param {Object} enums - The enum options to render input fields for.
   * @returns {JSX.Element} The JSX elements containing input fields for enum values.
   */
  const renderEnumInputs = (options) => {
    return options.map(({ value }) => (
      <TableRow key={`row-enum-${value}`}>
        <TableCell classes={{ root: classes.tableCell }} align="left">
          {value}
        </TableCell>
        <TableCell classes={{ root: classes.tableCell }} align="left">
          <TextField
            fullWidth
            variant="outlined"
            value={mappedObject?.customEnum[value]}
            onChange={handleEnumChange(value)}
            InputProps={{ classes: { root: classes.inputRoot } }}
          />
        </TableCell>
      </TableRow>
    ));
  };

  const renderValueTypeExtraOptions = () => {
    const isUnknownType = mappedObject.type === bacnetUnitTypes?.unknown?.value;
    const isTemperatureUnits =
      mappedObject.units in TEMPERATURE_UNITS || UNITS in TEMPERATURE_UNITS;

    return (
      <>
        {isTemperatureUnits && (
          <TableRow key={'row-units'}>
            <TableCell
              classes={{ root: clsx(classes.tableCell, classes.noPadding) }}
              align="left"
            >
              {t`Units`}
            </TableCell>
            <TableCell classes={{ root: classes.tableCell }} align="left">
              <Select
                fullWidth
                value={mappedObject.userUnits || UNITS}
                onChange={handleChange('userUnits')}
                classes={{ trigger: classes.inputRoot }}
                options={tempFieldOptions}
              />
            </TableCell>
          </TableRow>
        )}
        {!isUnknownType &&
          !bacNetObject.isReadOnly &&
          defaultValueTypeOption.map((key) => {
            const isStep = key === 'step';
            const isInvalidStep =
              isStep &&
              (!mappedObject.customEnum.step ||
                isNaN(mappedObject.customEnum.step) ||
                parseFloat(mappedObject.customEnum.step) <= 0);

            return (
              <TableRow
                key={`row-enum-${key}`}
                className={clsx(isStep && classes.stepRow)}
              >
                <TableCell
                  classes={{ root: clsx(classes.tableCell, classes.noPadding) }}
                  align="left"
                >
                  {key?.toUpperCase()}
                </TableCell>
                <TableCell classes={{ root: classes.tableCell }} align="left">
                  <TextField
                    fullWidth
                    disabled={mappedObject.readOnly}
                    variant="outlined"
                    type={'text'}
                    value={mappedObject.customEnum[key]}
                    onChange={handleEnumChange(key)}
                    InputProps={{ classes: { root: classes.inputRoot } }}
                    inputProps={{
                      min: isStep ? 1 : undefined,
                    }}
                    error={isInvalidStep}
                    helperText={
                      isStep && isInvalidStep
                        ? t`Step must be greater than 0`
                        : undefined
                    }
                    FormHelperTextProps={{ className: classes.stepHelperText }}
                  />
                </TableCell>
              </TableRow>
            );
          })}
      </>
    );
  };

  const renderEnumerationTypeExtraOption = () => {
    return (
      <div className={classes.enumerationsFormWrapper}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell
                classes={{
                  root: clsx(classes.tableCellBold, classes.borderBottom),
                }}
                align="left"
              ></TableCell>
              <TableCell
                classes={{
                  root: clsx(classes.tableCell, classes.borderBottom),
                }}
                align="left"
              ></TableCell>
              <TableCell
                classes={{
                  root: clsx(classes.tableCell, classes.borderBottom),
                }}
                align="left"
              >
                {t`Show`}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {bacnetEnumerationsTemplate.map(({ key }) => {
              const handleCheckboxChange = (event, value) => {
                setDirty(true);
                setMappedObject((prevParam) => {
                  if (!prevParam.customEnumVisibility) {
                    prevParam.customEnumVisibility = {};
                  }

                  return {
                    ...prevParam,
                    customEnumVisibility: {
                      ...prevParam.customEnumVisibility,
                      [key]: value,
                    },
                  };
                });
              };

              return (
                <TableRow key={`row-enum-${key}`}>
                  <TableCell classes={{ root: classes.tableCell }} align="left">
                    {key.toUpperCase()}
                  </TableCell>
                  <TableCell classes={{ root: classes.tableCell }} align="left">
                    <TextField
                      fullWidth
                      disabled={mappedObject.readOnly}
                      variant="outlined"
                      value={mappedObject.customEnum[key]}
                      onChange={handleEnumChange(key)}
                      InputProps={{ classes: { root: classes.inputRoot } }}
                    />
                  </TableCell>
                  <TableCell classes={{ root: classes.tableCell }} align="left">
                    <Checkbox
                      disabled={mappedObject.readOnly}
                      checked={mappedObject.customEnumVisibility[key] ?? true}
                      onChange={handleCheckboxChange}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    );
  };

  /**
   * Renders the advanced settings input fields based on the object type.
   *
   * @returns {JSX.Element|null} The JSX elements containing advanced settings or null if not applicable.
   */
  const renderAdvancedSettings = () => {
    const { binary, reverseBinary, enumeration, value, unknown } =
      bacnetUnitTypes || {};

    switch (mappedObject.type) {
      case binary?.value:
        return renderEnumInputs(binaryOptions);
      case reverseBinary?.value:
        return renderEnumInputs(reverseBinaryOptions);
      case value?.value:
      case unknown?.value:
        return renderValueTypeExtraOptions();
      case enumeration?.value:
        return renderEnumerationTypeExtraOption();
      default:
        return null;
    }
  };

  const renderTypeDependentSettings = () => {
    const { enumeration = {} } = bacnetUnitTypes || {};

    if (mappedObject.type === enumeration.value) {
      return (
        <TableRow key={'row-advanced-settings'}>
          <TableCell
            colSpan={2}
            className={clsx(classes.tableCell, classes.borderBottom)}
          >
            <div className={classes.enumerationCellContent}>
              <div className={classes.valueCellsTitle}>{t`Values`}</div>
              <div>{renderAdvancedSettings()}</div>
            </div>
          </TableCell>
        </TableRow>
      );
    }

    return (
      <TableRow key={'row-advanced-settings'}>
        <TableCell
          classes={{
            root: clsx(
              classes.tableCell,
              classes.tableCellBold,
              classes.borderBottom
            ),
          }}
          align="left"
        />
        <TableCell
          classes={{ root: clsx(classes.tableCell, classes.borderBottom) }}
          align="left"
        >
          {renderAdvancedSettings()}
        </TableCell>
      </TableRow>
    );
  };

  const enumerationsList = useMemo(() => {
    if (!enumeration || typeof enumeration !== 'object') {
      return [];
    }

    return Object.entries(enumeration).map(([key, value]) => ({ key, value }));
  }, [enumeration]);

  const renderEnumerationsList = () => {
    if (!enumeration) {
      return null;
    }

    return (
      <div className={classes.enumerationsListWrapper}>
        {enumerationsList.map(({ key, value }) => {
          return (
            <div key={key}>
              {key} - {value}
            </div>
          );
        })}
      </div>
    );
  };

  const getObjectTypeDisplayValue = (bacnetType) => {
    const name = bacnetObjectTypes[bacnetType];
    if (!name) {
      return bacnetType;
    }
    return `${name} (${bacnetType})`;
  };

  const bacnetParamsList = useMemo(() => {
    if (!bacNetObject || !bacnetObjectTypes) {
      return [];
    }

    const {
      name,
      objectId,
      bacnetType,
      maximumValue,
      minimumValue,
      valueStep,
    } = bacNetObject;

    const type = getObjectTypeDisplayValue(bacnetType);
    const mesurmentUnit = unitsFieldOptions?.[UNITS]?.label || '';

    return [
      { name: t`Name`, value: name },
      { name: t`Object ID`, value: objectId },
      { name: t`Type`, value: type },
      //{name:t`COV support`,value:getBinaryParamDisplayValue(isCovSupported)},
      //{name:t`Read only`,value:getBinaryParamDisplayValue(isReadOnly)},
      { name: t`Max value`, value: maximumValue },
      { name: t`Min value`, value: minimumValue },
      { name: t`Value step`, value: valueStep },
      //todo: In the future we should consider replacing `isEnumeration` with `customCellClassName` for more flexibility.
      {
        name: t`Enumerations`,
        value: renderEnumerationsList(),
        isEnumeration: true,
      },
      {
        name: t`Measurement units`,
        value: mesurmentUnit,
      },
    ];
  }, [bacNetObject, bacnetObjectTypes, unitsFieldOptions]);

  //This will be restored in the future. do not delete.
  /*  const renderTypeSelectOptions = () => {
    return bacnetUnitOptions.map((key) => {
      const isDisabled = key === 'enumeration' && !bacnetEnumerationsTemplate.length;
      return (
        <MenuItem
          key={key}
          value={bacnetUnitTypes[key]?.value}
          disabled={isDisabled}
        >
          {bacnetUnitTypes[key]?.label}
        </MenuItem>
      );
    });
  }; */

  const handleShowActivePrioritiesDialog = () => {
    setShowActivePrioritiesDialog(!showActivePrioritiesDialog);
  };

  const handleMappingChange = (newMapping) => {
    setMappedObject((prev) => ({
      ...prev,
      ...newMapping,
    }));
    setDirty(true);
  };

  const renderUniversalMappingSetting = () => {
    if (!template.entityTemplate) {
      return null;
    }
    return (
      <UniversalMappingSettings
        classes={classes}
        template={template}
        onMappingChange={handleMappingChange}
        mappedObject={mappedObject}
      />
    );
  };

  /**
   * Renders the content of the dialog, including BACnet object parameters and their mappings.
   *
   * @returns {JSX.Element} The JSX elements representing the dialog content.
   */
  const renderContent = () => {
    if (!mappedObject) {
      return null;
    }

    return (
      <Grid container spacing={2} style={{ flexWrap: 'nowrap', width: 'auto' }}>
        <Grid
          item
          style={{ flexShrink: 0, width: 'fit-content' }}
          className={classes.leftSideContainer}
        >
          <TableContainer style={{ overflow: 'hidden' }}>
            <Table stickyHeader aria-label="a dense table">
              <TableHead>
                <TableRow>
                  <TableCell
                    classes={{ root: classes.tableHeadCell }}
                    align="left"
                  >
                    {t`BACnet Param Info`}
                  </TableCell>
                  <TableCell
                    classes={{ root: classes.tableHeadCell }}
                    align="left"
                  >
                    {t`Value`}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {bacnetParamsList.map((param) => {
                  const { name, value, isEnumeration } = param;
                  const className = isEnumeration
                    ? classes.enumerationCellStyles
                    : '';
                  return (
                    <TableRow key={`param-${name}`}>
                      <TableCell
                        classes={{
                          root: clsx(
                            classes.tableCell,
                            classes.tableCellBold,
                            classes.borderBottom
                          ),
                        }}
                        align="left"
                        className={className}
                      >
                        {name}
                      </TableCell>
                      <TableCell
                        classes={{
                          root: clsx(classes.tableCell, classes.borderBottom),
                        }}
                        align="left"
                        className={className}
                      >
                        {value || '-'}
                      </TableCell>
                    </TableRow>
                  );
                })}
                <TableRow key="current-value-row">
                  <TableCell
                    classes={{
                      root: clsx(
                        classes.tableCell,
                        classes.tableCellBold,
                        classes.borderBottom
                      ),
                    }}
                    align="left"
                  >
                    {t`Current Value`}
                  </TableCell>
                  <TableCell
                    classes={{
                      root: clsx(classes.tableCell, classes.borderBottom),
                    }}
                    align="left"
                  >
                    <div className={classes.currentValueRow}>
                      {!isRawSupported ? (
                        <LightTooltip
                          classes={{ tooltip: classes.versionTooltip }}
                          title={t`Value retrieval requires EDGE version 1.9.0 or higher`}
                        >
                          <div className={classes.currentValueActionsWrapper}>
                            <IconButton
                              disabled
                              size="small"
                              className={classes.valueRefreshBtn}
                            >
                              <Refresh width={16} height={16} />
                            </IconButton>
                            <span className={classes.currentValueDisplay}>
                              {getDisplayValue(displayedCurrentValue)}
                            </span>
                            <span className={classes.setNewValueLabel}>
                              {t`Set New Value`}
                            </span>
                            {renderWriteValueControl()}
                            <FlowButton
                              disabled
                              width={75}
                              className={classes.setButton}
                              label={t`SET`}
                            />
                          </div>
                        </LightTooltip>
                      ) : (
                        <div className={classes.currentValueActionsWrapper}>
                          <LightTooltip
                            classes={{ tooltip: classes.versionTooltip }}
                            title={t`Click to retrieve value`}
                          >
                            <span>
                              <IconButton
                                size="small"
                                className={clsx(
                                  classes.valueRefreshBtn,
                                  isRefreshingValue && classes.spinning
                                )}
                                onClick={onRefreshValue}
                                disabled={isRefreshingValue}
                              >
                                <Refresh width={16} height={16} />
                              </IconButton>
                            </span>
                          </LightTooltip>
                          <span className={classes.currentValueDisplay}>
                            {getDisplayValue(displayedCurrentValue)}
                          </span>
                          <span className={classes.setNewValueLabel}>
                            {t`Set New Value`}
                          </span>
                          {renderWriteValueControl()}
                          <LightTooltip
                            classes={{ tooltip: classes.versionTooltip }}
                            title={
                              isSaveButtonDisabled
                                ? t`Modify value to enable SET button`
                                : t`click to set the value to BACnet device`
                            }
                          >
                            <span>
                              <FlowButton
                                disabled={isSaveButtonDisabled}
                                onClick={handleSetValue}
                                width={75}
                                className={classes.setButton}
                                label={t`SET`}
                              />
                            </span>
                          </LightTooltip>
                        </div>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
        <Grid
          item
          xs
          style={{ minWidth: 0 }}
          className={classes.rightSideContainer}
        >
          <TableContainer>
            <Table stickyHeader aria-label="a dense table">
              <TableHead className={classes.tableHead}>
                <TableRow>
                  <TableCell
                    classes={{ root: classes.tableHeadCell }}
                    align="left"
                    style={{ width: '40%' }}
                  >
                    {t`Mapped Value`}
                  </TableCell>
                  <TableCell
                    classes={{ root: classes.tableHeadCell }}
                    align="left"
                    style={{ width: '60%' }}
                  ></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow key={'row-name'}>
                  <TableCell
                    classes={{
                      root: clsx(
                        classes.tableCell,
                        classes.tableCellBold,
                        classes.borderBottom
                      ),
                    }}
                    align="left"
                  >
                    {t`Name`}
                  </TableCell>
                  <TableCell
                    classes={{
                      root: clsx(classes.tableCell, classes.borderBottom),
                    }}
                    align="left"
                  >
                    <TextField
                      fullWidth
                      variant="outlined"
                      placeholder={t`Name`}
                      value={mappedObject.name}
                      onChange={handleChange('name')}
                      InputProps={{ classes: { root: classes.inputRoot } }}
                    />
                  </TableCell>
                </TableRow>

                <TableRow key={'row-type'}>
                  <TableCell
                    classes={{
                      root: clsx(
                        classes.tableCell,
                        classes.borderBottom,
                        classes.tableCellBold
                      ),
                    }}
                    align="left"
                  >
                    {t`Type`}
                  </TableCell>
                  <TableCell
                    classes={{
                      root: clsx(classes.tableCell, classes.borderBottom),
                    }}
                    align="left"
                  >
                    {typeNameByTypeId[mappedObject.type] ?? t`Unknown`}
                    {/* This will be restored in the future. do not delete */}
                    {/* <Select
                      fullWidth
                      variant="outlined"
                      value={mappedObject.type}
                      onChange={handleChange('type')}
                      displayEmpty
                      classes={{ select: classes.inputRoot }}
                      disabled={!!objectType}
                    >
                      {renderTypeSelectOptions()}
                    </Select> */}
                  </TableCell>
                </TableRow>
                {renderTypeDependentSettings()}
              </TableBody>
              {renderUniversalMappingSetting()}
              <TableBody>
                <TableRow key={'row-error-point-enabled'}>
                  <TableCell
                    classes={{
                      root: clsx(
                        classes.tableCell,
                        classes.tableCellBold,
                        classes.checkboxContainer,
                        !mappedObject.errorPointEnabled && classes.borderBottom
                      ),
                    }}
                    align="left"
                  >
                    <Checkbox
                      checked={mappedObject.errorPointEnabled}
                      onChange={handleChange('errorPointEnabled')}
                    />
                    {t`Set as Error point`}
                  </TableCell>
                  <TableCell
                    classes={{
                      root: clsx(
                        classes.tableCell,
                        !mappedObject.errorPointEnabled && classes.borderBottom
                      ),
                    }}
                    style={{ width: '60%' }}
                  />
                </TableRow>

                {mappedObject.errorPointEnabled && (
                  <TableRow key={'row-error-point-value'}>
                    <TableCell
                      classes={{
                        root: clsx(classes.tableCell, classes.borderBottom),
                      }}
                      align="left"
                    >
                      {t`Error Clear Value`}
                    </TableCell>
                    <TableCell
                      classes={{
                        root: clsx(classes.tableCell, classes.borderBottom),
                      }}
                      align="left"
                    >
                      <TextField
                        fullWidth
                        variant="outlined"
                        defaultValue={0}
                        value={mappedObject.errorPointValue}
                        onChange={handleChange('errorPointValue')}
                        InputProps={{ classes: { root: classes.inputRoot } }}
                      />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
              <TableBody>
                <TableRow>
                  <TableCell
                    classes={{
                      root: clsx(classes.tableHeadCell, classes.tableCellBold),
                    }}
                    align="left"
                    style={{ width: '40%' }}
                  >
                    {t`Settings`}
                  </TableCell>
                  <TableCell
                    classes={{ root: classes.tableHeadCell }}
                    align="left"
                    style={{ width: '60%' }}
                  ></TableCell>
                </TableRow>
                <TableRow key={'row-RO'}>
                  <TableCell
                    classes={{
                      root: clsx(
                        classes.tableCell,
                        classes.tableCellBold,
                        classes.borderBottom
                      ),
                    }}
                    align="left"
                  >
                    {t`Read-only`}
                  </TableCell>
                  <TableCell
                    classes={{
                      root: clsx(classes.tableCell, classes.borderBottom),
                    }}
                    align="left"
                  >
                    <Select
                      menuPortal
                      value={mappedObject.readOnly}
                      onChange={handleChange('readOnly')}
                      classes={{ trigger: classes.inputRoot }}
                      options={Object.keys(roTypes).map((key) => ({
                        value: parseInt(key) === 1 ? true : false,
                        label: roTypes[key]?.toUpperCase(),
                      }))}
                    />
                  </TableCell>
                </TableRow>
                {
                  // Priority is to be hidden if the object is read-only.
                  mappedObject.readOnly || !isCommandable ? null : (
                    <>
                      <TableRow key={'row-priority'}>
                        <TableCell
                          classes={{
                            root: clsx(
                              classes.tableCell,
                              classes.tableCellBold,
                              classes.borderBottom
                            ),
                          }}
                          align="left"
                        >
                          {t`Priority`}
                        </TableCell>
                        <TableCell
                          classes={{
                            root: clsx(classes.tableCell, classes.borderBottom),
                          }}
                          align="left"
                        >
                          <Select
                            menuPortal
                            value={mappedObject?.priority}
                            onChange={handleChange('priority')}
                            classes={{ trigger: classes.inputRoot }}
                            options={priorityFieldsOptions.map(
                              ({ value, label }) => ({
                                value,
                                label: `${value} - ${label}`,
                              })
                            )}
                          />
                        </TableCell>
                      </TableRow>
                      <TableRow key={'row-active-priority'}>
                        <TableCell
                          classes={{
                            root: clsx(
                              classes.tableCell,
                              classes.tableCellBold,
                              classes.borderBottom
                            ),
                          }}
                          align="left"
                        />
                        <TableCell
                          classes={{
                            root: clsx(classes.tableCell, classes.borderBottom),
                          }}
                          align="left"
                        >
                          <Button
                            onClick={handleShowActivePrioritiesDialog}
                            marginRight
                            white
                            width={150}
                          >
                            {t`Active Priorities`}
                          </Button>
                        </TableCell>
                      </TableRow>
                    </>
                  )
                }
                <TableRow key={'row-COV'}>
                  <TableCell
                    classes={{
                      root: clsx(
                        classes.tableCell,
                        classes.tableCellBold,
                        classes.borderBottom
                      ),
                    }}
                    align="left"
                  >
                    {t`COV`}
                  </TableCell>
                  <TableCell
                    classes={{
                      root: clsx(classes.tableCell, classes.borderBottom),
                    }}
                    align="left"
                  >
                    <Select
                      menuPortal
                      value={mappedObject.cov}
                      onChange={handleChange('cov')}
                      classes={{ trigger: classes.inputRoot }}
                      disabled={covFieldsProps.disabled}
                      options={bacnetCovOptions.map((key) => ({
                        value: key,
                        label: bacnetCovOptionsTypes[key]?.toUpperCase(),
                      }))}
                    />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
    );
  };

  const handleCloseMappingDialog = async () => {
    if (!dirty) {
      handleClose();
      return;
    }
    const ok = await confirm({
      bodyMessage: t`Do you want to discard changes?`,
      confirmText: t`Yes`,
      cancelText: t`No`,
    });
    if (ok) {
      handleClose();
    }
  };

  return (
    <Dialog
      disableEnforceFocus
      fullScreen
      classes={{ paper: classes.dialogPaper }}
      aria-labelledby="simple-dialog-title"
      open={true}
    >
      <div className={classes.dialogHeader}>
        <div className={classes.headerTitleContainer}>
          <DynamicCircleIcon
            color={color}
            content={shortName}
            title={fullName}
            width={32}
            height={32}
          />
          <Typography className={classes.headerTitle}>{name}</Typography>
        </div>
        <IconButton
          disableRipple
          className={classes.iconBtnStyle}
          onClick={handleCloseMappingDialog}
        >
          <Close color="#7f7692" />
        </IconButton>
      </div>
      <DialogContent id="dialogContent" className={classes.dialogContent}>
        {renderContent()}
      </DialogContent>
      <DialogActions className={classes.actionsContainer}>
        <Button
          onClick={handleCloseMappingDialog}
          marginRight
          white
          width={150}
        >
          {t`Cancel`}
        </Button>
        <Button
          disabled={!dirty || !isValid}
          onClick={handleSaveChanges}
          width={150}
        >
          {t`save`}
        </Button>
      </DialogActions>
      {showActivePrioritiesDialog && (
        <ActivePrioritiesDialog
          bacNetObjectId={bacNetObject.objectId}
          handleClose={handleShowActivePrioritiesDialog}
          bacnetUnitId={bacnetUnitId}
        />
      )}
    </Dialog>
  );
};

export default ParamMappingDialog;
